/**
 * Functions.js
 * 
 * This module provides utility functions for managing setbacks in the SightFlight Designer application.
 * These functions handle the UI interaction, area calculations, and cleanup operations
 * related to setbacks on polygon segments.
 * 
 * The functions in this module serve as a bridge between the UI components and
 * the underlying geometric operations defined in other modules.
 */

import { POLYGONS } from "../../Global/variables";
import { getPolyArea } from "../AreaPitchAzimu";
import { addSetbackToEdge, updateWireframe } from "./Setbacks";
import { sceneManager } from "../../SetUps/SceneManager";
import { refreshSegmentPanelOverlapHighlights } from "../../Panels/ValidationVisuals";

/**
 * Updates the setback UI and internal data based on user input
 * 
 * Behaviour:
 * - If value is valid → apply it.
 * - If value would push the setback polygon outside, or make it too tiny →
 *      → REVERT to previous geometry and previous values (acts as a "cap").
 * 
 * Example:
 *  - 108" works, 109" invalid → after typing 109 or 1000, UI and geometry snap back to 108".
 * 
 * @param {number} segIndex - Index of the segment/polygon in the POLYGONS array
 * @param {Function} setInputValues - React state setter function for input field values (edge-wise)
 * @param {number} inputValue - The new setback value (in inches)
 * @param {number|undefined} edgeIndex - Index of the specific edge to update, or undefined to update all edges
 */
export function updateSetbackUI(segIndex, setInputValues, inputValue, edgeIndex) {
    const segment = POLYGONS[segIndex];
    let setbacks = segment.setBacks.edgesData;
    const scene = sceneManager.scene;

    // Snapshot previous setback geometry + values (for clamping/revert)
    const prevSetbacks = setbacks.map(edge => ({
        vertexId1: edge.vertexId1,
        vertexId2: edge.vertexId2,
        value: edge.value,
        point1: edge.point1.clone(),
        point2: edge.point2.clone()
    }));

    // Mark this polygon as having setbacks (we might flip it back on revert)
    segment.setBacks.isSet = true;

    const updatingAll = (edgeIndex === undefined || edgeIndex === null);

    /**
     * Helper: apply geometry for a given value (in inches), without touching the UI yet.
     */
    const applyGeometry = (valueInches) => {
        // Reset setbacks to previous snapshot before applying a new value
        setbacks.forEach((edge, i) => {
            edge.vertexId1 = prevSetbacks[i].vertexId1;
            edge.vertexId2 = prevSetbacks[i].vertexId2;
            edge.value = prevSetbacks[i].value;
            edge.point1.copy(prevSetbacks[i].point1);
            edge.point2.copy(prevSetbacks[i].point2);
        });

        if (updatingAll) {
            setbacks.forEach((_edge, i) => {
                addSetbackToEdge(setbacks, i, valueInches, segIndex);
                setbacks[i].value = valueInches;
            });
        } else {
            const idx = Number(edgeIndex);
            addSetbackToEdge(setbacks, idx, valueInches, segIndex);
            setbacks[idx].value = valueInches;
        }
    };

    // ------------------------------
    // Special case: inputValue == 0 → hard reset
    // ------------------------------
    if (updatingAll && inputValue == 0) {
        const edges = segment.edges;

        // Reset each setback edge to match the original polygon edge
        setbacks.forEach((edge, index) => {
            edge.point1.copy(edges[index].point1);
            edge.point2.copy(edges[index].point2);
            edge.value = 0;
        });

        // Update the wireframe visualization BACK to polygon edges
        updateWireframe(edges, segIndex);

        // Optional color to indicate reset
        if (segment.setBacks.mesh && segment.setBacks.mesh.material) {
            segment.setBacks.mesh.material.setValues({ color: 0xffffff }); // White
            segment.setBacks.mesh.material.needsUpdate = true;
        }

        segment.setBacks.isSet = false;

        if (segment.setBacks.mesh) {
            scene.remove(segment.setBacks.mesh);
        }

        // Reset UI inputs to 0
        setInputValues(prev => {
            const next = { ...prev };
            for (let i = 0; i < setbacks.length; i++) {
                next[i] = 0;
            }
            return next;
        });

        refreshSegmentPanelOverlapHighlights(segIndex);

        return;
    }

    // ------------------------------
    // 1) Try to apply new geometry
    // ------------------------------
    applyGeometry(inputValue);

    // Update wireframe with the new geometry
    updateWireframe(segment.setBacks.edgesData, segIndex);

    // ------------------------------
    // 2) Validate (relaxed cap)
    // ------------------------------
    // We intentionally allow setbacks to apply freely, even when the resulting
    // outline sits close to (or crosses) the roof edge on small sections. The
    // only value we reject is one that collapses/inverts the polygon into a
    // zero-or-negative area, which would produce broken/degenerate geometry.
    const currentArea = getPolyArea(segment.setBacks.edgesData); // sq ft

    const degenerate = !Number.isFinite(currentArea) || currentArea <= 0;

    const isValid = !degenerate;

    if (!isValid) {
        // ------------------------------
        // 3) INVALID → REVERT to previous valid state
        // ------------------------------
        console.warn(
            "Setback collapsed the section to a zero/negative area – reverting to previous valid state for segment",
            segIndex
        );

        // Restore previous setbacks
        segment.setBacks.edgesData = prevSetbacks.map(edge => ({
            vertexId1: edge.vertexId1,
            vertexId2: edge.vertexId2,
            value: edge.value,
            point1: edge.point1.clone(),
            point2: edge.point2.clone()
        }));
        setbacks = segment.setBacks.edgesData;

        // Restore isSet flag based on whether any previous value was non-zero
        segment.setBacks.isSet = prevSetbacks.some(e => e.value !== 0);

        // Restore wireframe
        updateWireframe(setbacks, segIndex);

        // Restore UI values (this acts as the "cap")
        setInputValues(prev => {
            const next = { ...prev };
            if (updatingAll) {
                prevSetbacks.forEach((edge, i) => {
                    next[i] = edge.value || 0;
                });
            } else {
                const idx = Number(edgeIndex);
                next[idx] = prevSetbacks[idx].value || 0;
            }
            return next;
        });

        refreshSegmentPanelOverlapHighlights(segIndex);

        return;
    }

    // ------------------------------
    // 4) VALID → Commit UI values
    // ------------------------------
    setInputValues(prev => {
        const next = { ...prev };
        if (updatingAll) {
            setbacks.forEach((edge, i) => {
                next[i] = inputValue;
            });
        } else {
            const idx = Number(edgeIndex);
            next[idx] = inputValue;
        }
        return next;
    });
    refreshSegmentPanelOverlapHighlights(segIndex);
}

/**
 * Updates the available area calculation for a segment's setbacks
 * 
 * This function calculates the area enclosed by the setback edges
 * and updates both the internal data structure and the UI display.
 * 
 * @param {number} index - Index of the segment/polygon in the POLYGONS array
 * @param {Function} setAvailableArea - React state setter function for the available area
 */
export function updateAvaArea(index, setAvailableArea) {
    // Calculate the area of the polygon formed by the setback edges
    // The area is in square feet, rounded to 2 decimal places
    let area = getPolyArea(POLYGONS[index].setBacks.edgesData).toFixed(2);
    
    // Store the calculated area in the polygon data
    POLYGONS[index].setBacks.avaArea = area;
    
    // Update the UI with the available area value
    setAvailableArea(area);
}

/**
 * Deletes all setbacks from a segment, resetting to the original polygon edges
 * 
 * This function removes any existing setbacks and resets the segment
 * to its original state without setbacks.
 * 
 * Used for explicit "Clear/Reset setbacks" actions.
 * 
 * @param {number} segmentIndex - Index of the segment/polygon in the POLYGONS array
 */
export function deleteSetback(segmentIndex) {
    // Get the selected segment/polygon
    let selectedSeg = POLYGONS[segmentIndex];
    
    // Get the original edges of the polygon
    let edges = selectedSeg.edges;
    
    // Create a new array to hold the reset setback data
    let data = [];
    
    // For each original edge, create a new setback with value 0 
    // that matches the original edge exactly
    edges.forEach(edge => {
        const setback = {
            vertexId1: edge.vertexId1,
            vertexId2: edge.vertexId2,
            value: 0,
            point1: edge.point1.clone(), // Clone to avoid reference issues
            point2: edge.point2.clone()
        };
        data.push(setback);
    });
    
    // Remove the existing setback mesh from the scene
    let setback = selectedSeg.setBacks;
    removeMesh(setback);
    
    // Replace the setback data with the reset data
    selectedSeg.setBacks.edgesData = data;
    
    // Mark this polygon as not having setbacks
    selectedSeg.setBacks.isSet = false;
    refreshSegmentPanelOverlapHighlights(segmentIndex);
}

/**
 * Removes a mesh from the scene and properly disposes of its resources
 * 
 * This utility function handles the cleanup of THREE.js resources
 * when a mesh is no longer needed, preventing memory leaks.
 * 
 * @param {Object} setback - The setback object containing the mesh to remove
 */
export function removeMesh(setback) {
    if (!setback || !setback.mesh) return;

    const mesh = setback.mesh;

    // Remove the mesh from the scene
    sceneManager.scene.remove(mesh);
    
    // Dispose of the geometry to free GPU resources
    if (mesh.geometry) {
        mesh.geometry.dispose();
    }
    
    // Dispose of the material to free GPU resources
    if (mesh.material) {
        mesh.material.dispose();
    }
    
    // Set the mesh reference to null for garbage collection
    setback.mesh = null;
}
